import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import SelectPortal from '@/components/Select/SelectPortal';
import TreeModal from '@/components/Select/TreeModal';
import treeSelectUtils from '@/components/Select/treeSelectUtils';
import type { TreeSelectItemBase } from '@/components/Select/types';
import useStates from '@/hooks/pure/useStates';
import { usePopup } from '@/stores/pure/PopupStore';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';
import { isEmptyString } from '@/utils/util';

type Props = {
  structuredField: StructuredField;
  status: boolean;
  close: () => void;
  pos: CSSProperties;
  initailValues?: StructuredFilterQueryFormatT;
  onChange: (values: StructuredFilterQueryFormatT) => void;
};

const FieldItemSelect = (props: Props) => {
  const { structuredField, status, close, pos } = props;
  const setPopup = usePopup();
  const cursorRef = useRef<string | null>(null);

  const [dataList, setDataList] = useState<TreeSelectItemBase<string>[] | null>(
    null,
  );
  const [filteredList, setFilteredList] = useState<
    TreeSelectItemBase<string>[] | null
  >(null);
  const [searchValue, setSearchValue] = useState('');
  const [tempSelectedValues, setTempSelectedValues] =
    useState<StructuredFilterQueryFormatT>(
      props.initailValues || {
        columnKey: structuredField.columnKey,
        operator: STRUCTURED_FILTER_OPERATOR.OR,
        filterValues: [],
      },
    );

  const { state: fetchState, setValue: setFetchValue } = useStates<{
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
  }>({
    isFetching: false,
    isFetchingNextPage: false,
    hasNextPage: false,
  });

  const fetchData = useCallback(
    async (params?: StructuredColumnFetcherParams) => {
      if (structuredField.filterItemOptions?.type === 'select') {
        const data =
          await structuredField?.filterItemOptions?.filterItemFetcher?.(params);

        if (data) {
          const result = data.reduce((acc, item) => {
            acc.push({
              alias: item.filterKey,
              node: item.filterOptionNode,
              searchValue: item.searchValue || item.filterName,
              meta: item.filterName,
              checkOption: {
                disable: item.disableCheck,
              },
            });
            return acc;
          }, [] as TreeSelectItemBase<string>[]);

          if (isEmptyString(params?.searchValue)) {
            setDataList((prev) =>
              params?.cursor ? [...(prev || []), ...result] : result,
            );
          } else {
            setFilteredList((prev) =>
              params?.cursor ? [...(prev || []), ...result] : result,
            );
          }
        }
        return;
      }
      if (structuredField.filterItemOptions?.type === 'pagination') {
        if (params?.cursor) {
          setFetchValue('isFetchingNextPage', true);
        }
        const data =
          await structuredField?.filterItemOptions?.filterItemFetcher?.(params);

        if (params?.cursor) {
          setFetchValue('isFetchingNextPage', false);
        }
        if (data) {
          const result = data.reduce((acc, item) => {
            acc.push({
              alias: item.filterKey,
              node: item.filterOptionNode,
              searchValue: item.searchValue || item.filterName,
              meta: item.filterName,
              checkOption: {
                disable: item.disableCheck,
              },
            });
            return acc;
          }, [] as TreeSelectItemBase<string>[]);

          const pageSize =
            props.structuredField.filterItemOptions?.pageSize ||
            data.length - 1;
          const newCursor = data[pageSize]?.cursor || null;
          cursorRef.current = newCursor;
          setFetchValue('hasNextPage', !!newCursor);

          if (isEmptyString(params?.searchValue)) {
            setDataList((prev) =>
              params?.cursor ? [...(prev || []), ...result] : result,
            );
          } else {
            setFilteredList((prev) =>
              params?.cursor ? [...(prev || []), ...result] : result,
            );
          }
        }

        return;
      }
      if (structuredField.filterItemOptions?.type === 'tree') {
        setFetchValue('isFetching', true);
        const data =
          await structuredField?.filterItemOptions?.filterItemFetcher?.(params);

        if (data) {
          const asyncMapper = async (
            items: StructuredFilterItem[],
          ): Promise<TreeSelectItemBase<string>[]> => {
            const result: TreeSelectItemBase<string>[] = [];
            for (const item of items) {
              const childNodes = await item.childOptions?.fetcher(
                item.filterKey,
              );
              if (childNodes) {
                result.push({
                  alias: item.filterKey,
                  node: item.filterOptionNode,
                  searchValue: item.searchValue || item.filterName,
                  childNodes: await asyncMapper(childNodes),
                  meta: item.filterName,
                  checkOption: {
                    disable: item.disableCheck,
                  },
                });
              } else {
                result.push({
                  alias: item.filterKey,
                  node: item.filterOptionNode,
                  searchValue: item.searchValue || item.filterName,
                  meta: item.filterName,
                  checkOption: {
                    disable: item.disableCheck,
                  },
                });
              }
            }
            return result;
          };

          const treeData = await asyncMapper(data || []);
          if (isEmptyString(params?.searchValue)) {
            setDataList(treeData);
          } else {
            setFilteredList(treeData);
          }
        }
        setFetchValue('isFetching', false);
        return;
      }
    },
    [setFetchValue, structuredField.filterItemOptions],
  );

  useEffect(() => {
    fetchData({
      searchValue,
    });
  }, [fetchData, searchValue]);

  const displayData = useMemo(() => {
    if (searchValue) {
      return filteredList || [];
    }
    return dataList || [];
  }, [dataList, filteredList, searchValue]);

  const updateParentChildNode = (
    alias: StringNumberValue,
    items: TreeSelectItemBase<string>[],
  ) => {
    setDataList((prev) => {
      if (!prev) return prev;
      const newDataList = [...prev];
      const targetIndex = newDataList.findIndex((d) => d.alias === alias);
      if (targetIndex === -1) return prev;
      newDataList[targetIndex].childNodes = [...items];
      return newDataList;
    });
  };

  const onChange = (values: FilterKeyValue[]) => {
    setTempSelectedValues({
      ...tempSelectedValues,
      filterValues: values,
    });
    props.onChange({
      ...tempSelectedValues,
      filterValues: values,
    });
  };

  const onCheck = ({
    value,
    exceptSelf,
    exceptChild,
    action,
  }: {
    value: StringNumberValue;
    exceptSelf?: boolean;
    exceptChild?: boolean;
    action?: 'add' | 'remove';
  }) => {
    if (
      structuredField.validate &&
      !structuredField.validate.filterValidator(
        tempSelectedValues.filterValues,
        value,
      )
    ) {
      setPopup({
        popupTheme: 'negative',
        title: structuredField.validate.title,
        message: structuredField.validate.message,
        type: 'alert',
      });
      return;
    }

    const compositedList = [...(dataList || []), ...(filteredList || [])];
    const target = treeSelectUtils.findNode<string>(compositedList, value);
    if (!target) return;

    const childs = treeSelectUtils
      .getFlattenChildNodes(target.childNodes || [])
      .filter((data) => !data.checkOption?.disable);

    let newValues = [...tempSelectedValues.filterValues];

    const toggleItem = (
      item: TreeSelectItemBase<string>,
      action?: 'add' | 'remove',
      exceptSelf?: boolean,

      // action이 지정되지 않은 경우 toggle을 기본동작으로 실행함
    ) => {
      const valueIndex = newValues.findIndex(
        (value) => value.filterKey === item.alias,
      );
      if (item.checkOption?.disable) return valueIndex;
      if (exceptSelf) return valueIndex;
      switch (action) {
        case 'add': {
          if (valueIndex === -1) {
            newValues.push({
              filterKey: item.alias,
              filterName: item.searchValue,
            });
          }
          break;
        }

        case 'remove': {
          if (valueIndex !== -1) {
            newValues.splice(valueIndex, 1);
          }
          break;
        }

        default: {
          newValues.toggle(
            {
              filterKey: item.alias,
              filterName: item.meta || item.searchValue,
            },
            (value) => value.filterKey === item.alias,
          );
          break;
        }
      }
      return valueIndex;
    };

    const targetIndex = toggleItem(target, action, exceptSelf);

    if (!exceptChild) {
      for (const t of childs) {
        if (action) {
          toggleItem(t, action);
        } else {
          if (targetIndex === -1) {
            toggleItem(t, 'remove');
            toggleItem(t, 'add');
          } else {
            toggleItem(t, 'remove');
          }
        }
      }
    }

    onChange(newValues);
  };

  const onRangeChange = (values: RangeValue) => {
    setTempSelectedValues({
      ...tempSelectedValues,
      rangeValues: values,
    });
    props.onChange({
      ...tempSelectedValues,
      rangeValues: values,
    });
  };

  const fetchNextPage = () => {
    fetchData({
      searchValue,
      cursor: cursorRef.current,
    });
  };

  if (structuredField.filterItemOptions?.type === 'custom') {
    return (
      <SelectPortal
        close={props.close}
        style={{
          ...pos,
          top: ((pos.top as number) || 0) + 8,
        }}
        status={props.status}
      >
        {structuredField.filterItemOptions.Component?.({
          values: tempSelectedValues.filterValues,
          onChange,
        })}
      </SelectPortal>
    );
  }

  if (structuredField.filterItemOptions?.type === 'range') {
    return (
      <SelectPortal
        close={props.close}
        style={{
          ...pos,
          top: ((pos.top as number) || 0) + 8,
        }}
        status={props.status}
      >
        {structuredField.filterItemOptions.RangeComponent?.({
          values: tempSelectedValues?.rangeValues || {},
          onChange: onRangeChange,
        })}
      </SelectPortal>
    );
  }

  return (
    <TreeModal
      mode={'multiple'}
      status={status}
      dataList={displayData}
      updateParentChildNode={updateParentChildNode}
      searchable={true}
      values={tempSelectedValues.filterValues.map((value) => value.filterKey)}
      onCheck={onCheck}
      onSearchChange={setSearchValue}
      close={close}
      loading={fetchState.isFetching}
      pos={{
        ...pos,
        top: ((pos.top as number) || 0) + 8,
      }}
      paginatedOptions={
        structuredField.filterItemOptions?.type === 'pagination'
          ? {
              hasNextPage: fetchState.hasNextPage || false,
              fetchNextPage: fetchNextPage,
              isFetchingNextPage: fetchState.isFetchingNextPage || false,
            }
          : undefined
      }
    />
  );
};

export default FieldItemSelect;
